<script setup lang="ts">
import HubCard from '@ui/composites/HubCard/HubCard.vue'
import Avatar from '@ui/components/Avatar/Avatar.vue'
import HubPrivate from '/assets/images/hub-private.svg?component'
import { UiButton } from '@ui'

definePageMeta({
  layout: 'default',
})
const {
  public: { appLink },
} = useRuntimeConfig()

const localeRoute = useLocaleRoute()
const { t } = useI18n()

const route = useRoute()
const slug = computed(() => route.params.hub_slug as string)

const { data: apiData, error } =
  await useNuxtApp().$client.hubs.getHub.useQuery(slug)

if (error.value?.data?.httpStatus === 404) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found',
  })
}

const pageData = computed(() => apiData.value?.data)

useSeoMeta({
  title: pageData.value?.title,
  description: pageData.value?.description,
  ogImage: pageData.value?.mediaData?.coverImage
    ? `${appLink}/${pageData.value.mediaData?.coverImage.large}`
    : '',
  twitterCard: 'summary_large_image',
})

// async function onMint() {

//   if (isDisabledMint.value) return

//   isDisabledMint.value = true
//   console.log('✨ Mint')

//   if (pageData.value?.collectionAddress) {
//     await mint.mint(pageData.value?.collectionAddress, {
//       name: pageData.value?.title,
//       description: pageData.value?.description,
//       image: pageData.value?.images.mint,
//       externalUrl: pageData.value?.externalUrl,
//       whitelisted: pageData.value?.whitelisted,
//       mintPrice: pageData.value?.mintPrice,
//       whitelistMintPrice: pageData.value.whitelistMintPrice,
//     })
//   }
//   nftTokenId.value.length
//     ? (isDisabledMint.value = true)
//     : (isDisabledMint.value = false)
// }

// fallback

const detectAndFormatLinks = (value: string) => {
  if (!value) return ''
  value = value.toString()
  const searchValue = /(https?:\/\/[^\s]+)/gim
  const replaceValue =
    '<a href="$&" target="_blank" rel="noopener noreferrer">$&</a>'
  return value.replace(searchValue, replaceValue)
}
</script>

<template>
  <div class="flex flex-col mx-auto max-w-245 pt-6 <lg:mx-4">
    <div v-if="pageData">
      <HubCard
        :image="
          pageData.mediaData?.coverImage?.large
            ? `${appLink}/${pageData.mediaData?.coverImage?.large}`
            : ''
        "
        :coverImageDominantColors="
          pageData.mediaData
            ? Array.isArray(pageData.mediaData.coverImageDominantColors)
              ? pageData.mediaData.coverImageDominantColors
              : []
            : []
        "
      >
        <template #title> {{ pageData.title }} </template>
        <template v-if="pageData.members" #subheader>
          {{ pageData.members }} Members
        </template>
        <template #avatar>
          <Avatar
            :url="
              pageData.mediaData?.avatar?.small
                ? `${appLink}/${pageData.mediaData?.avatar?.small}`
                : ''
            "
          />
        </template>
        <template v-if="pageData.hubType === 'nftCollection'" #badge>
          <i
            class="i-belong:hub-verified min-w-4.75 max-w-4.75 min-h-5 max-h-5 ml-2"
          />
        </template>
      </HubCard>
      <div
        v-if="pageData.description"
        class="event-overview prose mt-8 max-w-[60.625rem] <md:mb-10"
      >
        <h5 class="font-500 tracking-wide text-left text-[#A3A3A3]">
          {{ t('hub-overview') }}
        </h5>
        <p
          class="mt-3.5 whitespace-pre-line"
          v-html="detectAndFormatLinks(pageData.description)"
        ></p>
      </div>
      <div
        :class="{
          'mt-18 ': !pageData.description,
          '<md:hidden': pageData.description,
        }"
        class="custom-hr mt-18 mb-13.5 !bg-[#2E2E2F]"
      />
      <div class="flex flex-col gap-3.5">
        <NuxtLink
          v-if="pageData.hubType === 'nftCollection'"
          :to="`${appLink}/payments?target=hub-minting&hubId=${pageData.id}`"
          class="buy-a-ticket bg-[#7540EE] border-[rgba(23,13,48,0.36)]"
          target="_blank"
        >
          <img
            alt=""
            src="~/assets/images/events/icon-nft-ticket.svg"
            class="mr-4 w-6.25 h-6.25"
          />
          {{ t('buy-a-ticket') }}
        </NuxtLink>

        <NuxtLink
          :to="`${appLink}/hubs/${pageData.id}`"
          class="buy-a-ticket border-[#626262]"
          target="_blank"
        >
          <img
            alt=""
            src="~/assets/images/events/icon-event-details.svg"
            class="mr-4 w-5 h-4.75"
          />
          {{ t('view-event-details') }}
        </NuxtLink>
      </div>
      <img
        alt=""
        src="~/assets/images/events/powered-by-belong.svg"
        class="mt-12 mx-auto mb-14 w-49 h-8"
      />
      <div class="flex justify-center">
        <NuxtLink :to="localeRoute('/hubs')">{{
          t('explore-all-hubs')
        }}</NuxtLink>
      </div>
    </div>
    <template v-if="error?.data?.httpStatus === 403">
      <div class="flex flex-col items-center pt-46.5 pb-60">
        <HubPrivate />
        <p class="mt-8 text-center px-20 sm:text-xl">
          {{ t('the-hub-or-event') }}
        </p>

        <div class="mt-16 flex flex-row flex-wrap justify-center gap-3.5">
          <NuxtLink :to="`${appLink}/intro`" target="_blank" class="w-59">
            <UiButton variant="full-purple">
              <p class="text-base font-400 sm:text-lg">
                {{ t('log-in') }}
              </p>
            </UiButton>
          </NuxtLink>
          <NuxtLink :to="localeRoute('/')" class="w-59">
            <UiButton variant="color-only-border" class="!border-[#2C2C2E]">
              <p class="text-base font-400 sm:text-lg">
                {{ t('back-to-homepage') }}
              </p></UiButton
            >
          </NuxtLink>
        </div>
      </div>
    </template>
  </div>
  <SiteFooter />
</template>

<style scoped></style>
